'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/inputs/field/styles.css';
import { Field, type FieldSize } from 'BomdisoftComponents/inputs/field/index';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { type ComponentProps, type ReactNode, useId, useState } from 'react';

type TextFieldCommonProps = {
  className?: string;
  endAdornment?: ReactNode;
  error?: boolean;
  helperText?: string;
  label?: string;
  /**
   * Offer a show/hide control on a password field. Off by default so a field
   * only reveals what it was asked to: a shoulder-surfing risk is a real cost,
   * and the caller knows whether the surface is one where it is worth paying.
   * Labels are passed in because this package holds no translation table.
   */
  revealable?: boolean;
  revealLabel?: string;
  hideLabel?: string;
  /** The same three steps a `Button` takes, with the same default, so a form's
   * inputs and its action buttons are never different heights. */
  size?: FieldSize;
  startAdornment?: ReactNode;
};

/** `size` is omitted from the native input props because its own `size` is a
 * numeric character-width, which would intersect this component's scale prop
 * down to `never`. A textarea has no `size` attribute to collide with. */
export type TextFieldProps = (TextFieldCommonProps & Omit<ComponentProps<'input'>, 'size'> & { multiline?: false }) | (TextFieldCommonProps & ComponentProps<'textarea'> & { multiline: true });

type ControlWiring = {
  'aria-describedby'?: string;
  'aria-invalid'?: ComponentProps<'input'>['aria-invalid'];
  id: string;
};

export const TextField = (props: TextFieldProps) => {
  const { className, endAdornment, error, helperText, hideLabel, label, revealable, revealLabel, size, startAdornment } = props;
  const reactId = useId();
  const [isRevealed, setIsRevealed] = useState(false);
  const inputId = props.id ?? `${reactId}-input`;
  const helperId = helperText ? `${reactId}-helper` : undefined;
  const wiring: ControlWiring = { 'aria-describedby': helperId ?? props['aria-describedby'], 'aria-invalid': error ? true : props['aria-invalid'], id: inputId };
  const canReveal = Boolean(revealable) && !props.multiline && props.type === 'password';
  return (
    <Field className={className} disabled={props.disabled} error={error} helperId={helperId} helperText={helperText} inputId={inputId} label={label} multiline={props.multiline} required={props.required} size={size}>
      {startAdornment && <span className='bomdisoft-field-adornment'>{startAdornment}</span>}
      {renderControl(props, wiring, canReveal && isRevealed)}
      {canReveal && (
        <Tooltip title={isRevealed ? hideLabel : revealLabel}>
          <button
            aria-controls={inputId}
            aria-label={isRevealed ? hideLabel : revealLabel}
            aria-pressed={isRevealed}
            className='bomdisoft-field-reveal'
            // Never a submit: on a login form the default type would send the
            // credentials instead of showing them.
            data-testid={`${inputId}-reveal`}
            disabled={props.disabled}
            onClick={() => setIsRevealed(current => !current)}
            type='button'
          >
            <RevealGlyph isRevealed={isRevealed} />
          </button>
        </Tooltip>
      )}
      {endAdornment && <span className='bomdisoft-field-adornment'>{endAdornment}</span>}
    </Field>
  );
};

const RevealGlyph = ({ isRevealed }: { isRevealed: boolean }) => (
  <svg aria-hidden='true' fill='none' stroke='currentColor' strokeWidth='1.7' viewBox='0 0 24 24'>
    <path d='M1.6 12S5.5 5 12 5s10.4 7 10.4 7-3.9 7-10.4 7S1.6 12 1.6 12z' />
    <circle cx='12' cy='12' r='3.2' />
    {isRevealed && <path d='M4 20L20 4' />}
  </svg>
);

const renderControl = (props: TextFieldProps, wiring: ControlWiring, isRevealed: boolean) => {
  if (props.multiline) {
    const { className, endAdornment, error, helperText, hideLabel, label, multiline, revealable, revealLabel, size, startAdornment, ...attrs } = props;
    return <textarea {...attrs} {...wiring} className='bomdisoft-field-input bomdisoft-field-textarea' />;
  }
  const { className, endAdornment, error, helperText, hideLabel, label, multiline, revealable, revealLabel, size, startAdornment, ...attrs } = props;
  return <input {...attrs} {...wiring} className='bomdisoft-field-input' type={isRevealed ? 'text' : attrs.type} />;
};
